import { PlusIcon, CreditCardIcon, SquareCheckIcon } from '../../../../../assets/icons';
import { xhrDefaultMessage } from '../../../../../configs/notification';
import { setNotification } from '../../../../../store/general/slice';
import { useDispatch, useSelector } from '../../../../../store/store';
import { PaymentMethod } from '../../../../../store/workspace/types';
import { GENERIC_ERROR } from '../../constants';
import CheckBox from '../../../../../components/CheckBox';
import { CheckoutStep } from '../../types';
import Spinner from '../../../../../components/Spinner';
import { useEffect, useState } from 'react';
import styles from './style.module.scss';
import Modal from '../../../../../components/Modal';
import {
    setAddNewCardModal,
    setPaymentRetryMode,
    setChooseCreditCardModal,
} from '../../../../../store/workspace/slice';
import {
    getProfileDataThunk,
    purchasePaymentThunk,
    getPaymentMethodsThunk,
    getSubsciptionHistoryThunk,
} from '../../../../../store/workspace/thunks';
import {
    selectPaymentMethods,
    selectPaymentRetryMode,
    selectSelectedPricingPlan,
    selectSelectedPlanSeatCount,
    selectFetchingPaymentMethods,
} from '../../../../../store/workspace/selectors';

/** Checkout: pays for the plan just picked, or retries an unpaid subscription. */
export default function ChooseCard() {
    const dispatch = useDispatch();
    const retryMode = useSelector(selectPaymentRetryMode);
    const paymentMethods = useSelector(selectPaymentMethods);
    const seatCount = useSelector(selectSelectedPlanSeatCount);
    const selectedPlan = useSelector(selectSelectedPricingPlan);
    const fetching = useSelector(selectFetchingPaymentMethods);
    const [errorMessage, setErrorMessage] = useState('');
    const [submitingPayment, setSubmitingPayment] = useState(false);
    const [step, setStep] = useState<CheckoutStep>('SelectPaymentMethod');
    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);

    const isDone = step === 'Done';
    const closeModal = () => dispatch(setChooseCreditCardModal(false));

    useEffect(() => {
        dispatch(getPaymentMethodsThunk({ action: 'get_payment_methods' }));

        /** The retry flow is a one-off, the next checkout has to opt in again. */
        return () => {
            dispatch(setPaymentRetryMode(false));
        };
    }, [dispatch]);

    const onSuccess = () => {
        setStep('Done');
        dispatch(setPaymentRetryMode(false));
        dispatch(getProfileDataThunk());
        dispatch(getSubsciptionHistoryThunk({ action: 'get_subscription_status' }));
        dispatch(setNotification(['payment has been successfully confirmed', 'success']));
    };

    const onFailure = (error: any) => {
        setErrorMessage(error?.message || xhrDefaultMessage);
        dispatch(setNotification([GENERIC_ERROR, 'error']));
    };

    const pay = () => {
        if (!selectedMethod) return;

        setErrorMessage('');
        setSubmitingPayment(true);

        /** Seats are billed as a suffix of the plan id, none of them means no suffix. */
        const body = retryMode ? {
            action: 'retry_payment_daily',
            payment_method_id: selectedMethod.id,
        } : {
            action: 'create_subscription',
            pmId: selectedMethod.id,
            plan_type: `${selectedPlan?.id}${seatCount === 0 ? '' : `_${seatCount}m`}`,
        };

        dispatch(purchasePaymentThunk(body))
            .unwrap()
            .then(onSuccess)
            .catch(onFailure)
            .finally(() => {
                setSubmitingPayment(false);
            });
    };

    return (
        <Modal
            size='sm'
            closeFunc={closeModal}
            childClassName={styles.wrapper}
            actions={isDone ? 'confirm' : 'both'}
            icon={isDone ? <SquareCheckIcon /> : <CreditCardIcon />}
            title={isDone ? 'Successfull payment' : 'Choose a card to continue'}
            dismissButton={{
                text: 'Cancel',
                type: 'Outline-Primary',
                onClick: closeModal,
            }}
            confirmButton={{
                text: isDone ? 'Confirm' : 'Pay',
                type: 'Primary',
                onClick: isDone ? closeModal : pay,
                disabled: !selectedMethod,
                loading: submitingPayment,
            }}
        >
            {!isDone && <>
                <span>Your Cards</span>
                {!fetching && paymentMethods.map(method => <div
                    key={method.id}
                    className={styles.card}
                    onClick={() => setSelectedMethod(method)}
                >
                    <CheckBox
                        checked={selectedMethod?.id === method.id}
                        onClick={() => setSelectedMethod(method)}
                    />
                    <div className={styles.card_info}>
                        <span>{method.card.brand}</span>
                        <span>Credit card ending in **** {method.card.last4}</span>
                    </div>
                </div>)}
                {fetching && <Spinner />}
                <div
                    className={styles.add_new_card}
                    onClick={() => dispatch(setAddNewCardModal(true))}
                >
                    <PlusIcon />
                    <span>Add new credit or debit card</span>
                </div>
            </>}

            {errorMessage && <span className={styles.error}>{errorMessage}</span>}

            {isDone && <span className={styles.successfull}>
                Your payment has been successfully confirmed.
            </span>}
        </Modal>
    );
};
